import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, MapPin, Calendar } from 'lucide-react';
import axios from 'axios';

const Squad = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('vets');
  const [vetsSubTeam, setVetsSubTeam] = useState('Crawley Athletic');

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await axios.get('/squad');
      setPlayers(response.data);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayers = players.filter(player => {
    if (filter === 'all') return true;
    if (filter === 'vets') {
      return player.team_type === 'Vets' && (player.vets_team || 'Crawley Athletic') === vetsSubTeam;
    }
    return player.team_type.toLowerCase() === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading squad...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Squad & Staff</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Meet the talented players and dedicated staff that represent Crawley Athletic
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="mb-8 flex justify-center">
          <div className="flex space-x-2 bg-white rounded-lg p-2 shadow">
            {['vets', 'staff'].map((filterOption) => (
              <button
                key={filterOption}
                data-testid={`squad-filter-${filterOption}`}
                onClick={() => setFilter(filterOption)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                  filter === filterOption
                    ? 'bg-black text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {filterOption === 'all' ? 'All Players' : `${filterOption} Team`}
              </button>
            ))}
          </div>
        </div>

        {/* Vets Sub-team Tabs */}
        {filter === 'vets' && (
          <div className="mb-8 flex justify-center">
            <div className="flex space-x-2 bg-gray-100 rounded-lg p-2">
              {['Crawley Athletic', 'Crawley RVB'].map((subTeam) => (
                <button
                  key={subTeam}
                  data-testid={`vets-subteam-${subTeam.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setVetsSubTeam(subTeam)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    vetsSubTeam === subTeam
                      ? 'bg-black text-white'
                      : 'text-gray-600 hover:bg-white'
                  }`}
                >
                  {subTeam}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Players Grid */}
        {filteredPlayers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No players found</h3>
            <p className="text-gray-600">
              {players.length === 0 
                ? "The squad information will be updated soon."
                : "No players match the selected filter."
              }
            </p>
          </div>
        ) : (
          <div className="player-grid">
            {filteredPlayers.map((player) => (
              <Link 
                key={player.id} 
                to={`/player/${player.id}`}
                className="player-card hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="relative">
                  {player.photo_url ? (
                    <img
                      src={player.photo_url}
                      alt={player.full_name}
                      className="player-image"
                    />
                  ) : (
                    <div className="w-full h-80 bg-gray-200 flex items-center justify-center">
                      <Users className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                  <div className="player-jersey">
                    {player.jersey_number}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {player.full_name} #{player.jersey_number}
                  </h3>
                  <p className="text-lg text-gray-600 mb-2">{player.position}</p>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    {player.nationality}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      player.team_type === 'Vets' 
                        ? 'bg-black text-white' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {player.team_type === 'Vets'
                        ? (player.vets_team || 'Crawley Athletic')
                        : player.team_type}
                    </span>
                  </div>

                  {player.bio && (
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {player.bio}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Squad;